import { useRouteRef } from '@backstage/core-plugin-api';
import {
  CREATABLE_ACTIVITY_WORK_PATHS,
  type CreatableActivityWorkPath,
} from '@internal/spassu-code-contract';
import { activityNewRouteRef, controlPlaneNewRouteRef } from '../routes';
import { normalizeRouteRefPath } from '../utils/routes';
import {
  ACTIVITY_WORK_PATH_COPY,
  type ActivityWorkPathCopy,
} from './contracts';

/**
 * Uma via criável oferecida na tela de escolha, com tudo que a tela precisa
 * saber sobre ela: texto, disponibilidade e destino.
 *
 * Derivar apenas a renderização do contrato não bastava: a navegação ficava num
 * ternário binário, então uma via criável nova apareceria na tela e seria
 * roteada para o Control Plane — falha silenciosa pior que a invisibilidade que
 * o ADR-005 quis eliminar.
 */
export interface ActivityWorkPathOption {
  readonly workPath: CreatableActivityWorkPath;
  readonly copy: ActivityWorkPathCopy;
  /**
   * Sempre presente. O opcional é o **resultado**: devolve `undefined` quando a
   * rota não está vinculada neste app, que é a degradação explicada de US-005.
   */
  readonly resolvePath: () => string | undefined;
}

/**
 * Um `useRouteRef` por via criável, chamado incondicionalmente e em ordem fixa —
 * hooks não podem ser condicionais. O `satisfies Record<...>` sobre o registro é
 * o que faz via criável nova **sem resolvedor** quebrar `yarn tsc`: a
 * obrigatoriedade está no resolvedor, não no seu resultado.
 */
export function useActivityWorkPathOptions(): readonly ActivityWorkPathOption[] {
  const frontierNewRoute = useRouteRef(activityNewRouteRef);
  const controlPlaneNewRoute = useRouteRef(controlPlaneNewRouteRef);

  const destinations = {
    'frontier-web': () => normalizeRouteRefPath(frontierNewRoute()),
    'control-plane': () => {
      const path = controlPlaneNewRoute?.();
      return path === undefined ? undefined : normalizeRouteRefPath(path);
    },
  } as const satisfies Record<
    CreatableActivityWorkPath,
    () => string | undefined
  >;

  return CREATABLE_ACTIVITY_WORK_PATHS.map(workPath => ({
    workPath,
    copy: ACTIVITY_WORK_PATH_COPY[workPath],
    resolvePath: destinations[workPath],
  }));
}
